import React, { useCallback, useState } from 'react';
import { sanitizeInput, validateEmail, validatePassword, validateText } from '../../utils/inputValidation';
import PolicyModal from './PolicyModal';
import TermsContent from './TermsContent';
import PrivacyContent from './PrivacyContent';
import Icon from '../Common/Icon';
import TurnstileWidget from './TurnstileWidget';
import './AuthForms.css';

/**
 * SignupForm Component
 *
 * Privacy-first signup form for ThryveLoop
 * - Email + password authentication
 * - Password strength validation
 * - Terms & privacy policy acceptance
 * - Email verification flow
 */
const SignupForm = ({ entryMode = 'web', onSignup, onSwitchToLogin, onNavigateToPrivacy, onNavigateToTerms, isLoading = false, error = null }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    acceptedTerms: false,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const isTurnstileEnabled = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, message: '' };

    let score = 0;
    const feedback = [];

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    else feedback.push('at least 12 characters');

    // Complexity checks
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      score++;
    } else {
      feedback.push('uppercase and lowercase letters');
    }

    if (/\d/.test(password)) {
      score++;
    } else {
      feedback.push('numbers');
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score++;
    } else {
      feedback.push('special characters');
    }

    // Common patterns to avoid
    const commonPatterns = ['password', '12345', 'qwerty', 'abc123'];
    if (commonPatterns.some(pattern => password.toLowerCase().includes(pattern))) {
      score = Math.max(0, score - 2);
      feedback.push('avoid common patterns');
    }

    // Generate message
    let message = '';
    if (score <= 1) message = 'Weak';
    else if (score === 2) message = 'Fair';
    else if (score === 3) message = 'Good';
    else if (score === 4) message = 'Strong';
    else message = 'Excellent';

    if (feedback.length > 0 && score < 4) {
      message += ` - Add ${feedback.join(', ')}`;
    }

    return { score, message };
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    let processedValue = newValue;

    if (name === 'email' && typeof newValue === 'string') {
      processedValue = sanitizeInput(newValue, { lowerCase: true });
    } else if (name === 'name' && typeof newValue === 'string') {
      processedValue = sanitizeInput(newValue);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: null,
    }));

    // Update password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  // Validate form with comprehensive security validation
  const validateForm = () => {
    const errors = {};

    // Email validation (with sanitization)
    const emailResult = validateEmail(formData.email);
    if (!emailResult.valid) {
      errors.email = emailResult.error;
    }

    // Name validation (optional, with XSS protection)
    if (formData.name) {
      const nameResult = validateText(formData.name, {
        maxLength: 100,
        allowHtml: false,
      });
      if (!nameResult.valid) {
        errors.name = nameResult.error;
      }
    }

    // Password validation (comprehensive strength check)
    const passwordResult = validatePassword(formData.password, {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
    });
    if (!passwordResult.valid) {
      errors.password = passwordResult.error;
    } else if (passwordResult.strength === 'weak') {
      errors.password = 'Please choose a stronger password';
    }

    // Confirm password
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance
    if (!formData.acceptedTerms) {
      errors.acceptedTerms = 'You must accept the terms and privacy policy';
    }

    if (isTurnstileEnabled && !turnstileToken) {
      errors.captcha = 'Please complete the security check.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setValidationErrors(prev => ({
      ...prev,
      captcha: null,
    }));
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setValidationErrors(prev => ({
      ...prev,
      captcha: 'The security check expired. Please try again.',
    }));
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setValidationErrors(prev => ({
      ...prev,
      captcha: 'Security check failed to load. Please refresh and try again.',
    }));
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Sanitize and prepare data for submission
    const emailResult = validateEmail(formData.email);
    const nameResult = formData.name ? validateText(formData.name, { allowHtml: false }) : { sanitized: null };

    // Call parent's onSignup handler with sanitized data and CAPTCHA token
    try {
      await onSignup({
        email: emailResult.sanitized,
        password: formData.password, // Password not sanitized (hashed on server)
        name: nameResult.sanitized,
        captchaToken: turnstileToken,
      });
    } finally {
      if (isTurnstileEnabled) {
        setTurnstileToken(null);
        setTurnstileResetKey((key) => key + 1);
      }
    }
  };

  // Get password strength color
  const getStrengthColor = (score) => {
    if (score <= 1) return '#ef4444'; // red
    if (score === 2) return '#f59e0b'; // orange
    if (score === 3) return '#3b82f6'; // blue
    return '#10b981'; // green
  };

  return (
    <div className="auth-form-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-form-header">
          <h2>{entryMode === 'lifetime' ? 'Create your desktop account' : 'Create your account'}</h2>
          <p className="auth-form-subtitle">
            {entryMode === 'lifetime'
              ? 'Use this account for Lifetime checkout, desktop activation, and optional encrypted cloud sync.'
              : 'Start with Explorer on the web. You can change your plan later in Settings.'}
          </p>
        </div>

        {error && (
          <div className="auth-error-banner" role="alert">
            {error}
          </div>
        )}

        {/* Name field (optional) */}
        <div className="form-group">
          <label htmlFor="name">
            Name <span className="optional-label">(optional)</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            autoComplete="name"
            disabled={isLoading}
          />
        </div>

        {/* Email field */}
        <div className="form-group">
          <label htmlFor="email">
            Email <span className="required-label">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            autoComplete="email"
            required
            disabled={isLoading}
            className={validationErrors.email ? 'input-error' : ''}
          />
          {validationErrors.email && (
            <span className="error-message">{validationErrors.email}</span>
          )}
        </div>

        {/* Password field */}
        <div className="form-group">
          <label htmlFor="password">
            Password <span className="required-label">*</span>
          </label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a strong password"
              autoComplete="new-password"
              required
              disabled={isLoading}
              className={validationErrors.password ? 'input-error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} strokeWidth={2.2} />
            </button>
          </div>
          {formData.password && (
            <div className="password-strength">
              <div className="password-strength-bar">
                <div
                  className="password-strength-fill"
                  style={{
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    backgroundColor: getStrengthColor(passwordStrength.score),
                  }}
                />
              </div>
              <span
                className="password-strength-text"
                style={{ color: getStrengthColor(passwordStrength.score) }}
              >
                {passwordStrength.message}
              </span>
            </div>
          )}
          {validationErrors.password && (
            <span className="error-message">{validationErrors.password}</span>
          )}
        </div>

        {/* Confirm password field */}
        <div className="form-group">
          <label htmlFor="confirmPassword">
            Confirm Password <span className="required-label">*</span>
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your password"
            autoComplete="new-password"
            required
            disabled={isLoading}
            className={validationErrors.confirmPassword ? 'input-error' : ''}
          />
          {validationErrors.confirmPassword && (
            <span className="error-message">{validationErrors.confirmPassword}</span>
          )}
        </div>

        {/* Terms acceptance */}
        <div className="form-group form-group-checkbox">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="acceptedTerms"
              checked={formData.acceptedTerms}
              onChange={handleChange}
              disabled={isLoading}
              className={validationErrors.acceptedTerms ? 'input-error' : ''}
            />
            <span>
              I agree to the{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsModal(true);
                }}
                style={{ color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacyModal(true);
                }}
                style={{ color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Privacy Policy
              </a>
            </span>
          </label>
          {validationErrors.acceptedTerms && (
            <span className="error-message">{validationErrors.acceptedTerms}</span>
          )}
        </div>

        {isTurnstileEnabled && (
          <div className="form-group turnstile-form-group">
            <label>
              Security check <span className="required-label">*</span>
            </label>
            <TurnstileWidget
              resetKey={turnstileResetKey}
              onVerify={handleTurnstileVerify}
              onExpire={handleTurnstileExpire}
              onError={handleTurnstileError}
            />
            {validationErrors.captcha && (
              <span className="error-message">{validationErrors.captcha}</span>
            )}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          className="btn-modern primary auth-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </button>

        {/* Switch to login */}
        <div className="auth-form-footer">
          <p>
            Already have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onSwitchToLogin}
              disabled={isLoading}
            >
              Log In
            </button>
          </p>
        </div>

        {/* Privacy notice */}
        <div className="privacy-notice">
          <p>
            <strong>Private by design:</strong> Personal records are encrypted on your device before cloud sync.
            Account and plan data stay separate from your personal content.
          </p>
        </div>
      </form>

      {/* Terms of Service Modal */}
      <PolicyModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        type="terms"
      >
        <TermsContent />
      </PolicyModal>

      {/* Privacy Policy Modal */}
      <PolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        type="privacy"
      >
        <PrivacyContent />
      </PolicyModal>
    </div>
  );
};

export default SignupForm;
